import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { homeService } from "../../services/homeService";
import { HomeAdItem, HomeNoticeItem } from "../../types/home";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

const BANNER_COLORS = ["#5B21B6", "#10b981", "#3b82f6", "#f59e0b", "#ef4444"];
const BOX_COLORS = ["#3b82f6", "#10b981", "#ef4444"];

// 앱 바텀탭 높이(44) + 마진(50) = 94px
const BOTTOM_TAB_OFFSET = 94;

function BannerSlide({
  adver,
  index,
  onClick,
}: {
  adver: HomeAdItem;
  index: number;
  onClick: () => void;
}) {
  return (
    <div
      className="relative w-full cursor-pointer rounded-2xl overflow-hidden"
      style={{ backgroundColor: "#EEEEEE" }}
      onClick={onClick}
    >
      {adver.imageUrl ? (
        <img
          src={adver.imageUrl}
          alt={adver.title}
          className="w-full object-contain"
          style={{ display: "block" }}
        />
      ) : (
        <div
          className="w-full flex items-center justify-center text-white text-xl font-bold"
          style={{
            backgroundColor: BANNER_COLORS[index % BANNER_COLORS.length],
            aspectRatio: "5 / 2",
          }}
        >
          {adver.title}
        </div>
      )}
    </div>
  );
}

function BoxAdCard({
  adver,
  index,
  onClick,
}: {
  adver: HomeAdItem;
  index: number;
  onClick: () => void;
}) {
  return (
    <div
      className="cursor-pointer rounded-2xl overflow-hidden"
      onClick={onClick}
    >
      {adver.imageUrl ? (
        <div className="w-full aspect-square bg-gray-100">
          <img
            src={adver.imageUrl}
            alt={adver.title}
            className="w-full h-full object-contain"
          />
        </div>
      ) : (
        <div
          className="w-full aspect-square flex items-center justify-center text-white text-sm font-bold"
          style={{ backgroundColor: BOX_COLORS[index % BOX_COLORS.length] }}
        >
          {adver.title}
        </div>
      )}
    </div>
  );
}

function NoticeCard({ notice }: { notice: HomeNoticeItem }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="bg-white rounded-2xl p-4 cursor-pointer active:opacity-80 transition-opacity"
      style={{ border: "1px solid #EEEEEE" }}
      onClick={() => setExpanded((prev) => !prev)}
    >
      <div className="flex items-start justify-between">
        <h3
          className="text-base font-bold flex-1 pr-2"
          style={{ color: "#333333" }}
        >
          {notice.isPinned && (
            <i className="fas fa-thumbtack text-orange-500 text-xs mr-1" />
          )}
          {notice.title}
        </h3>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm" style={{ color: "#999999" }}>
            {new Date(notice.createdAt).toLocaleDateString("ko-KR", {
              month: "2-digit",
              day: "2-digit",
            })}
          </span>
          <i
            className={`fas fa-chevron-down text-xs transition-transform duration-200`}
            style={{
              color: "#BBBBBB",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </div>
      </div>

      {/* 접힌 상태: content 2줄 미리보기 */}
      {!expanded && (
        <p
          className="text-sm line-clamp-2 leading-[21px] mt-2"
          style={{ color: "#666666" }}
        >
          {notice.content}
        </p>
      )}

      {/* 펼친 상태: content 전체 */}
      {expanded && (
        <p
          className="text-sm leading-[21px] mt-2 whitespace-pre-wrap"
          style={{ color: "#444444" }}
        >
          {notice.content}
        </p>
      )}
    </div>
  );
}

export default function Home() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["home"],
    queryFn: homeService.getHomeData,
  });

  const bannerAds = data?.bannerAds ?? [];
  const notices = data?.notices ?? [];
  const boxAds = data?.boxAds ?? [];

  const handleAdClick = (url: string) => {
    if (url) window.open(url, "_blank");
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center"
        style={{
          minHeight: `calc(100vh - ${BOTTOM_TAB_OFFSET}px)`,
          backgroundColor: "#F8F7F1",
        }}
      >
        <div className="text-center" style={{ color: "#999999" }}>
          <i className="fas fa-spinner fa-spin text-2xl mb-2" />
          <p className="text-sm mt-2">불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="flex items-center justify-center"
        style={{
          minHeight: `calc(100vh - ${BOTTOM_TAB_OFFSET}px)`,
          backgroundColor: "#F8F7F1",
        }}
      >
        <div className="text-center" style={{ color: "#999999" }}>
          <i className="fas fa-exclamation-circle text-2xl mb-2" />
          <p className="text-sm mt-2">데이터를 불러올 수 없습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col"
      style={{
        backgroundColor: "#F8F7F1",
        minHeight: `calc(100vh - ${BOTTOM_TAB_OFFSET}px)`,
      }}
    >
      {/* 배너 광고 슬라이드 — 항상 공간 확보 */}
      <div className="relative w-full px-4 pt-[18px]">
        {bannerAds.length > 0 ? (
          <>
            <style>{`
            .banner-swiper {
              overflow: hidden !important;
            }
            .banner-swiper .swiper-pagination {
              position: relative;
              margin-top: 10px;
              bottom: auto;
            }
            .banner-swiper .swiper-pagination-bullet {
              width: 6px;
              height: 6px;
              background: #CCCCCC;
              opacity: 1;
            }
            .banner-swiper .swiper-pagination-bullet-active {
              background: #333333;
              width: 18px;
              border-radius: 3px;
            }
          `}</style>
            <Swiper
              modules={[Autoplay, Pagination]}
              slidesPerView={1}
              spaceBetween={12}
              centeredSlides={false}
              // 폴더블/태블릿(≥640px)에서만 좌우 peek 효과
              breakpoints={
                bannerAds.length > 1
                  ? {
                      640: {
                        slidesPerView: 1.08,
                        spaceBetween: 8,
                        centeredSlides: true,
                      },
                    }
                  : undefined
              }
              pagination={bannerAds.length > 1 ? { clickable: true } : false}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              loop={bannerAds.length > 1}
              className="banner-swiper"
              style={{ overflow: "hidden" }}
            >
              {bannerAds.map((adver, index) => (
                <SwiperSlide key={adver.id}>
                  <BannerSlide
                    adver={adver}
                    index={index}
                    onClick={() => handleAdClick(adver.linkUrl)}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </>
        ) : (
          <div
            className="w-full rounded-2xl"
            style={{ aspectRatio: "5 / 2", backgroundColor: "#EEEEEE" }}
          />
        )}
      </div>

      {/* 공지사항 섹션 */}
      {notices.length > 0 && (
        <div className="px-4 pt-[16px]">
          <h2
            className="text-[20px] font-bold mb-[16px]"
            style={{ color: "#222222" }}
          >
            공지사항
          </h2>
          <div className="space-y-2">
            {notices.map((notice) => (
              <NoticeCard key={notice.id} notice={notice} />
            ))}
          </div>
        </div>
      )}

      {/* 공지사항과 박스 광고 사이 공간 */}
      <div className="flex-1" />

      {/* 하단 박스 광고 섹션 */}
      {boxAds.length > 0 && (
        <div className="px-4 pt-[16px] pb-4">
          <div className="grid grid-cols-3 gap-3">
            {boxAds.map((adver, index) => (
              <BoxAdCard
                key={adver.id}
                adver={adver}
                index={index}
                onClick={() => handleAdClick(adver.linkUrl)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
